"use client";

import Link from "next/link";
import { Header, Footer } from "@/components/common";

const SUPPORT_EMAIL = "support@key-stone.app";

const ShieldIcon = ({ className = "w-4 h-4" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M12 2L4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M9 12l2 2 4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = ({ className = "w-4 h-4" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M5 12.5l4.5 4.5L19 7.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CrossIcon = ({ className = "w-4 h-4" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ArrowIcon = ({ className = "w-4 h-4" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const inScopeAssets = [
  "Production environments of our official Shopify applications listed in the Keystone apps portfolio.",
  "Core application logic, APIs, and integrations powering those production apps.",
  "Authentication flows used by merchants to install and configure Keystone apps.",
];

const outOfScopeAssets = [
  "Marketing and corporate websites where no merchant or customer data is processed.",
  "Staging, development, or QA environments.",
  "Third-party services we rely on, including Shopify, Stripe, analytics providers, and support tools such as Zendesk and Intercom.",
  "The Shopify platform itself. Issues affecting the Shopify core platform should be reported to Shopify directly.",
];

const inScopeVulns = [
  {
    title: "Remote Code Execution & Path Traversal",
    desc: "LFI, OS command injection, unsafe deserialization, and similar critical execution flaws.",
  },
  {
    title: "Authentication & Authorization Flaws",
    desc: "Privilege escalation, IDOR, broken session handling, and OAuth or SSO misconfiguration.",
  },
  {
    title: "Server-Side Request Forgery (SSRF)",
    desc: "Requests that reach internal infrastructure or sensitive metadata endpoints.",
  },
  {
    title: "Injection Attacks",
    desc: "SQL, NoSQL, GraphQL, and Server-Side Template Injection (SSTI).",
  },
  {
    title: "Cross-Site Scripting (XSS)",
    desc: "Stored, reflected, and DOM-based XSS with a realistic exploitation path.",
  },
  {
    title: "Sensitive Data Exposure",
    desc: "Leakage of merchant or customer PII, hardcoded secrets, and exposed API keys.",
  },
  {
    title: "Cryptographic Weaknesses",
    desc: "Broken encryption, predictable tokens, and weak random number generation.",
  },
  {
    title: "Business Logic Flaws",
    desc: "Workflow abuse, pricing manipulation, and subscription or billing bypass.",
  },
];

const outOfScopeVulns = [
  {
    title: "Infrastructure attacks",
    desc: "DoS, DDoS, volumetric or load-based testing, and physical security.",
  },
  {
    title: "Social engineering",
    desc: "Phishing or vishing targeting Keystone employees, contractors, or merchants.",
  },
  {
    title: "Third-party vulnerabilities",
    desc: "Issues in Shopify, Stripe, or other upstream platforms we integrate with.",
  },
  {
    title: "Email configuration",
    desc: "SPF, DKIM, and DMARC findings without a demonstrated exploit.",
  },
  {
    title: "Scanner noise & theoretical issues",
    desc: "Automated tool output without a proven impact or working proof of concept.",
  },
  {
    title: "Missing best practices",
    desc: "Headers, cookie flags, or version banners without demonstrated impact.",
  },
  {
    title: "Self-exploitation",
    desc: "Self-XSS or attacks requiring unrealistic user interaction.",
  },
  {
    title: "Authentication UX issues",
    desc: "Logout CSRF, account enumeration, and brute-force without rate-limit bypass.",
  },
];

const processSteps = [
  {
    step: "01",
    title: "Submit your report",
    desc: `Email a detailed write-up to ${SUPPORT_EMAIL} including affected assets, reproduction steps, payloads, and any supporting screenshots or proof-of-concept material.`,
  },
  {
    step: "02",
    title: "Acknowledgement",
    desc: "We aim to acknowledge every valid submission within two business days and assign a tracking reference for ongoing communication.",
  },
  {
    step: "03",
    title: "Triage & validation",
    desc: "Our security team reproduces the issue, assigns a CVSS v3.1 score, and confirms whether the report falls within scope.",
  },
  {
    step: "04",
    title: "Remediation",
    desc: "We target fixes within seven days for Critical and High severity findings, and within 30 to 90 days for Medium and Low severity issues.",
  },
  {
    step: "05",
    title: "Coordinated disclosure",
    desc: "We follow a 90-day embargo from the date of acknowledgement before any public discussion of the issue.",
  },
  {
    step: "06",
    title: "Recognition",
    desc: "With the researcher's consent, we publish their name, an optional link, and the severity of the finding in our Hall of Fame.",
  },
];

const principles = [
  "Use only your own test stores, accounts, or data. Do not access another merchant's or customer's information.",
  "Stop as soon as you have demonstrated impact. Do not exfiltrate, modify, or delete data beyond what is strictly required to prove the finding.",
  "Avoid actions that would degrade service for merchants or their shoppers. No automated load, brute force, or destructive testing.",
  "Give us a reasonable window to remediate before any public disclosure. Do not extort or threaten public release.",
  "Comply with all applicable laws while conducting your research.",
];

const navLinks = [
  { href: "#scope", label: "Scope" },
  { href: "#process", label: "Process" },
  { href: "#guidelines", label: "Guidelines" },
  { href: "#hall-of-fame", label: "Hall of Fame" },
];

export default function SecurityClient() {
  return (
    <div className="min-h-screen bg-[#212121]">
      <Header />

      {/* Hero */}
      <section className="pt-40 pb-16 bg-[#212121]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(248,247,244,0.15)] bg-[rgba(248,247,244,0.04)] text-[#F8F7F4] text-xs font-medium tracking-[0.08em] uppercase mb-6">
            <ShieldIcon className="w-3.5 h-3.5" />
            Responsible Disclosure
          </div>

          <h1 className="text-4xl lg:text-5xl font-medium tracking-tight text-[#F8F7F4] mb-4">
            Security & Responsible Disclosure
          </h1>
          <p className="text-lg text-[#A4A4A4] max-w-3xl">
            Security is foundational to how we build software for Shopify
            merchants. If you believe you have found a vulnerability in one of
            our products, we want to hear from you — and we want to credit your
            work.
          </p>

          <div className="flex flex-wrap items-center gap-3 mt-8">
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#F8F7F4] text-[#212121] text-sm font-medium hover:bg-white transition-colors"
            >
              <ShieldIcon className="w-4 h-4" />
              Report a vulnerability
              <ArrowIcon className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </a>
            <a
              href="#process"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[rgba(248,247,244,0.18)] text-[#F8F7F4] text-sm font-medium hover:border-[rgba(248,247,244,0.4)] hover:bg-[rgba(248,247,244,0.04)] transition-colors"
            >
              How the process works
            </a>
          </div>

          <p className="text-sm text-[#909090] mt-8">
            Last updated: 22 May 2026
          </p>
        </div>
      </section>

      {/* Anchor nav */}
      <div className="sticky top-0 z-20 bg-[#212121]/85 backdrop-blur-md border-y border-[rgba(96,96,96,0.3)]">
        <div className="container mx-auto px-6 max-w-6xl">
          <nav className="flex items-center gap-1 overflow-x-auto py-3 -mx-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="px-3 py-1.5 rounded-full text-sm text-[#A4A4A4] hover:text-[#F8F7F4] hover:bg-[rgba(248,247,244,0.04)] transition-colors whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="ml-auto hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm text-[#F8F7F4] hover:bg-[rgba(248,247,244,0.06)] transition-colors whitespace-nowrap"
            >
              {SUPPORT_EMAIL}
              <ArrowIcon className="w-3.5 h-3.5" />
            </a>
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-6 max-w-6xl pb-24">
        <div className="space-y-16 pt-16">
          {/* Intro */}
          <section>
            <p className="text-base leading-relaxed text-[#A4A4A4] mb-4">
              Shop Circle Holdings Ltd (trading as Keystone), One Kingdom
              Street, Paddington Central, London W2 6BD, United Kingdom
              (&quot;we,&quot; &quot;us,&quot; &quot;Keystone&quot; and
              &quot;our&quot;) operates a Responsible Disclosure Program for
              security researchers who help us identify and resolve
              vulnerabilities in our production applications.
            </p>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              The program is recognition-based: we do not currently offer
              monetary rewards, but we will publicly credit valid reporters in
              our Hall of Fame. Please review the scope and rules below before
              submitting a report — submissions that follow these guidelines
              will be triaged faster and have a higher chance of being
              accepted.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Target Assets */}
          <section id="scope">
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-2">
              Target Assets
            </h2>
            <p className="text-base text-[#A4A4A4] mb-8">
              Where to look — and where not to.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-[rgba(96,96,96,0.3)] bg-[rgba(248,247,244,0.02)] p-6">
                <div className="flex items-center gap-2 mb-5">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[rgba(248,247,244,0.06)] border border-[rgba(248,247,244,0.15)] text-[#F8F7F4]">
                    <CheckIcon className="w-3.5 h-3.5" />
                  </span>
                  <h3 className="text-base font-medium text-[#F8F7F4] tracking-tight">
                    In Scope
                  </h3>
                </div>
                <ul className="space-y-3">
                  {inScopeAssets.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-base text-[#A4A4A4] leading-relaxed"
                    >
                      <CheckIcon className="w-4 h-4 mt-1 flex-shrink-0 text-[#F8F7F4]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl border border-[rgba(96,96,96,0.3)] bg-[rgba(248,247,244,0.02)] p-6">
                <div className="flex items-center gap-2 mb-5">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[rgba(248,247,244,0.06)] border border-[rgba(248,247,244,0.15)] text-[#909090]">
                    <CrossIcon className="w-3.5 h-3.5" />
                  </span>
                  <h3 className="text-base font-medium text-[#F8F7F4] tracking-tight">
                    Out of Scope
                  </h3>
                </div>
                <ul className="space-y-3">
                  {outOfScopeAssets.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-base text-[#A4A4A4] leading-relaxed"
                    >
                      <CrossIcon className="w-4 h-4 mt-1 flex-shrink-0 text-[#909090]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Vulnerability Scope */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-2">
              Vulnerability Scope
            </h2>
            <p className="text-base text-[#A4A4A4] mb-8">
              The categories we actively accept and prioritise — and those we
              consider out of scope.
            </p>

            <p className="text-xs uppercase tracking-[0.15em] text-[#909090] mb-4">
              In-Scope Categories
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-10">
              {inScopeVulns.map((v) => (
                <div
                  key={v.title}
                  className="rounded-xl border border-[rgba(96,96,96,0.3)] bg-[rgba(248,247,244,0.02)] p-5 hover:border-[rgba(248,247,244,0.18)] transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <CheckIcon className="w-4 h-4 mt-1 flex-shrink-0 text-[#F8F7F4]" />
                    <div>
                      <h3 className="text-[15px] font-medium text-[#F8F7F4] tracking-tight mb-1">
                        {v.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-[#A4A4A4]">
                        {v.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs uppercase tracking-[0.15em] text-[#909090] mb-4">
              Out-of-Scope Categories
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {outOfScopeVulns.map((v) => (
                <div
                  key={v.title}
                  className="rounded-xl border border-[rgba(96,96,96,0.3)] bg-[rgba(248,247,244,0.02)] p-5"
                >
                  <div className="flex items-start gap-3">
                    <CrossIcon className="w-4 h-4 mt-1 flex-shrink-0 text-[#909090]" />
                    <div>
                      <h3 className="text-[15px] font-medium text-[#F8F7F4] tracking-tight mb-1">
                        {v.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-[#A4A4A4]">
                        {v.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Resolution Process */}
          <section id="process">
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-2">
              Resolution Process
            </h2>
            <p className="text-base text-[#A4A4A4] mb-8">
              From report to recognition — here&apos;s how a submission moves
              through our team.
            </p>

            <ol className="grid md:grid-cols-2 gap-3">
              {processSteps.map((s) => (
                <li
                  key={s.step}
                  className="rounded-xl border border-[rgba(96,96,96,0.3)] bg-[rgba(248,247,244,0.02)] p-6 hover:border-[rgba(248,247,244,0.18)] transition-colors"
                >
                  <div className="flex items-start gap-5">
                    <span
                      className="flex-shrink-0 text-3xl font-medium tracking-tight text-[#909090] tabular-nums"
                      style={{ lineHeight: "1" }}
                    >
                      {s.step}
                    </span>
                    <div>
                      <h3 className="text-base font-medium text-[#F8F7F4] tracking-tight mb-1">
                        {s.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-[#A4A4A4]">
                        {s.desc}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Researcher Guidelines */}
          <section id="guidelines">
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-2">
              Researcher Guidelines
            </h2>
            <p className="text-base text-[#A4A4A4] mb-8">
              We ask all researchers to act in good faith. Following these
              principles keeps your report eligible and our merchants safe.
            </p>
            <ul className="space-y-3">
              {principles.map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-3 text-base text-[#A4A4A4] leading-relaxed"
                >
                  <CheckIcon className="w-4 h-4 mt-1.5 flex-shrink-0 text-[#F8F7F4]" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Hall of Fame */}
          <section id="hall-of-fame">
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-2">
              Hall of Fame
            </h2>
            <p className="text-base text-[#A4A4A4] mb-8">
              We publicly credit researchers whose reports lead to a confirmed
              fix. Want to be the first name on this list?
            </p>

            <div className="rounded-2xl border border-dashed border-[rgba(96,96,96,0.5)] bg-[rgba(248,247,244,0.015)] p-10 text-center">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[rgba(248,247,244,0.04)] border border-[rgba(248,247,244,0.12)] text-[#F8F7F4] mb-4">
                <ShieldIcon className="w-5 h-5" />
              </span>
              <p className="text-base text-[#F8F7F4] font-medium mb-2">
                Our Hall of Fame is just getting started.
              </p>
              <p className="text-sm text-[#A4A4A4]">
                Submit a valid vulnerability and we&apos;ll credit you here
                with your name, an optional link, and the severity of your
                finding.
              </p>
            </div>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Rewards */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Rewards
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              This is a recognition-only program. We do not currently offer
              monetary rewards or bounty payments. Researchers participate in
              the program for credit in our Hall of Fame and to help us keep
              Shopify merchants safe.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Legal Safe Harbour */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Legal Safe Harbour
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4] mb-4">
              We consider research conducted in line with this policy to be
              authorised research. We will not pursue or support legal action
              against researchers who, in good faith, comply with the rules of
              this program, including the scope and researcher guidelines set
              out above.
            </p>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              If legal action is initiated by a third party against a
              researcher who has complied with this policy, we will make it
              known that the activity was authorised. This safe harbour does
              not apply to research that violates applicable law or that falls
              outside of this policy.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Changes */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Changes
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              We may update this policy from time to time to reflect changes in
              our practices, legal requirements, or for other operational,
              legal, or regulatory reasons. We encourage you to review this
              policy periodically.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Governing Law
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4]">
              This policy and any disputes arising out of or in connection with
              it shall be governed by and construed in accordance with the laws
              of England and Wales. The courts of England and Wales shall have
              non-exclusive jurisdiction.
            </p>
          </section>

          <div className="border-t border-[rgba(96,96,96,0.3)]" />

          {/* Contact */}
          <section id="contact">
            <h2 className="text-2xl font-medium tracking-tight text-[#F8F7F4] mb-4">
              Contact
            </h2>
            <p className="text-base leading-relaxed text-[#A4A4A4] mb-6">
              For all security-related communication, including vulnerability
              reports and questions about this policy, reach out to our team
              directly.
            </p>

            <div className="rounded-2xl border border-[rgba(96,96,96,0.3)] bg-[rgba(248,247,244,0.02)] p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-[#909090] mb-1">
                  Contact email
                </p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-xl font-medium text-[#F8F7F4] hover:text-white transition-colors"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="group inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#F8F7F4] text-[#212121] text-sm font-medium hover:bg-white transition-colors"
              >
                <ShieldIcon className="w-4 h-4" />
                Report a vulnerability
                <ArrowIcon className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </a>
            </div>

            <p className="text-sm text-[#909090] mt-6">
              For privacy or data-subject requests, see our{" "}
              <Link
                href="/privacy-policy"
                className="text-[#A4A4A4] underline hover:text-[#F8F7F4]"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
